// APFloatingField — CSS-first floating label, the v4 form default (spec
// § Decisions 2026-07-02). Visual identity ports v3's FloattingLabelBox
// (src/components/antdWrappers/FloattingLabelBox.tsx: 14px label → scales to
// label size on the border, 200ms ease, solid patch over the border) — the
// implementation is pure Tailwind state selectors, no fieldset/legend, no JS
// focus tracking:
//   - `peer` children (Input/Textarea): float on `peer-focus` or
//     `peer-[:not(:placeholder-shown)]` — the control carries
//     `peer placeholder-transparent` + `placeholder=" "`.
//   - `group` children (Radix Select / APMultiSelect triggers): float on
//     `group-focus-within`, open state, or value present (no
//     `[data-placeholder]` in the subtree).
// Error state needs no wiring: FormLabel reads the RHF field context and sets
// `data-error` (→ destructive label) while the control gets `aria-invalid`
// (→ destructive border).
import { ReactNode } from 'react';

import { FormLabel, useFormField } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';

export type FloatingMode = 'peer' | 'group';

interface FloatingLabelClassOptions {
  mode: FloatingMode;
  alignTop?: boolean;
  error?: boolean;
  required?: boolean;
}

const floatingLabelClasses = ({ mode, alignTop, error, required }: FloatingLabelClassOptions) =>
  cn(
    // Resting: transparent patch (the label sits INSIDE the control,
    // whose dark-mode bg is tinted). Floated: solid patch masking the
    // border the label rides on. The patch surface is --ap-floating-patch
    // (defaults to background) — surfaces that are NOT bg-background (the
    // bg-card AI side panel) override the var on an ancestor so the patch
    // matches and no chip seam shows in dark.
    'pointer-events-none absolute start-2 z-10 max-w-[calc(100%-1.5rem)] truncate rounded-sm bg-transparent px-1 text-sm font-normal text-muted-foreground transition-all duration-200 motion-reduce:transition-none',
    alignTop ? 'top-4.5' : 'top-1/2',
    '-translate-y-1/2',
    mode === 'peer' && [
      'peer-focus:top-0 peer-focus:bg-[var(--ap-floating-patch,var(--background))] peer-focus:text-xs',
      'peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:bg-[var(--ap-floating-patch,var(--background))] peer-[:not(:placeholder-shown)]:text-xs',
      'peer-disabled:opacity-50',
      !error && 'peer-focus:text-primary',
    ],
    mode === 'group' && [
      'group-focus-within/floating:top-0 group-focus-within/floating:bg-[var(--ap-floating-patch,var(--background))] group-focus-within/floating:text-xs',
      'group-has-[[data-state=open]]/floating:top-0 group-has-[[data-state=open]]/floating:bg-[var(--ap-floating-patch,var(--background))] group-has-[[data-state=open]]/floating:text-xs',
      'group-[:not(:has([data-placeholder]))]/floating:top-0 group-[:not(:has([data-placeholder]))]/floating:bg-[var(--ap-floating-patch,var(--background))] group-[:not(:has([data-placeholder]))]/floating:text-xs',
      'group-has-[:disabled]/floating:opacity-50',
      !error && 'group-focus-within/floating:text-primary',
    ],
    required && "after:ms-0.5 after:text-destructive after:content-['*']"
  );

export interface APFloatingFieldProps {
  /** Already-translated label — doubles as the resting placeholder. */
  label: ReactNode;
  required?: boolean;
  /** `peer` — native input/textarea child; `group` — Radix trigger child. */
  mode: FloatingMode;
  /** Rest the label on the first line (textarea) instead of the vertical center. */
  alignTop?: boolean;
  className?: string;
  children: ReactNode;
}

export const APFloatingField = ({
  label,
  required,
  mode,
  alignTop,
  className,
  children,
}: APFloatingFieldProps) => {
  const { error } = useFormField();
  return (
    // min-w-0: FormItem is a grid — without it this wrapper tracks the nowrap
    // trigger's max-content and a long value (subject name) overflows the cell
    // instead of letting the trigger's truncate engage (P2-41a).
    <div className={cn('relative min-w-0', mode === 'group' && 'group/floating', className)}>
      {children}
      <FormLabel className={floatingLabelClasses({ mode, alignTop, error: !!error, required })}>
        {label}
      </FormLabel>
    </div>
  );
};

export interface APFloatingLabelProps extends APFloatingFieldProps {
  /** Mirror the control's invalid state (no RHF context to read it from). */
  error?: boolean;
  /** Control id — gives the overlay label its accessible-name association. */
  htmlFor?: string;
}

/** Standalone floating label for non-RHF surfaces (plain controlled dialogs) —
 *  same visual identity as APFloatingField, error state supplied by prop. */
export const APFloatingLabel = ({
  label,
  required,
  mode,
  alignTop,
  error,
  htmlFor,
  className,
  children,
}: APFloatingLabelProps) => (
  // min-w-0: P2-41a parity with APFloatingField — in grid/flex parents the
  // wrapper must not track a nowrap trigger's max-content width.
  <div className={cn('relative min-w-0', mode === 'group' && 'group/floating', className)}>
    {children}
    <Label
      htmlFor={htmlFor}
      data-error={!!error}
      className={cn(
        floatingLabelClasses({ mode, alignTop, error: !!error, required }),
        error && 'text-destructive'
      )}
    >
      {label}
    </Label>
  </div>
);
